import { createFileRoute } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VerifiedMixologistBadge } from "@/components/VerifiedMixologistBadge";
import { adminListMixologists, adminDecideMixologist } from "@/lib/mixologist.functions";

export const Route = createFileRoute("/_authenticated/admin/mixologists")({
  head: () => ({ meta: [{ title: "Mixologist CV Scrutiny — Admin" }] }),
  component: AdminMixologistsPage,
});

type Applicant = {
  id: string;
  full_name: string | null;
  mixologist_track_status: string | null;
  mixologist_advanced_score: number | null;
  mixologist_advanced_total: number | null;
  mixologist_advanced_completed_at: string | null;
  mixologist_bar_manager_answer: string | null;
  mixologist_reviewed_at: string | null;
  mixologist_review_notes: string | null;
  mixologist_cheating_strikes: number | null;
  latest_attempt: {
    score: number | null;
    total: number | null;
    one_strike_terminated: boolean | null;
    cheating_terminated: boolean | null;
    created_at: string;
    time_remaining_per_question: Record<string, number> | null;
    events_count: number;
  } | null;
};

function AdminMixologistsPage() {
  const list = useServerFn(adminListMixologists);
  const decide = useServerFn(adminDecideMixologist);
  const [items, setItems] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const refresh = async () => {
    setLoading(true);
    const r = await list();
    if ("ok" in r) setItems(r.applicants as Applicant[]);
    else toast.error(r.error);
    setLoading(false);
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDecide = async (user_id: string, decision: "approved" | "rejected") => {
    const r = await decide({ data: { user_id, decision, notes: notes[user_id] ?? "" } });
    if ("error" in r) {
      toast.error(r.error);
      return;
    }
    toast.success(`Marked as ${decision}.`);
    await refresh();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="px-6 py-5 border-b border-border">
        <BackToDashboard className="mb-2" />
        <p className="tracking-wide-luxury text-accent text-xs">Admin</p>
        <h1 className="font-display text-2xl">Mixologist CV Scrutiny Pipeline</h1>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-8">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No mixologist applicants yet.</p>
        ) : (
          <ol className="space-y-6">
            {items.map((a) => (
              <li key={a.id} className="bg-white border border-border p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-lg">{a.full_name ?? "Unnamed"}</p>
                      <VerifiedMixologistBadge
                        status={a.mixologist_track_status}
                        reviewedAt={a.mixologist_reviewed_at}
                        size="sm"
                      />
                      {a.mixologist_track_status === "rejected_mixologist" && (
                        <span className="inline-flex items-center px-2 py-1 text-[0.6rem] border border-destructive/40 text-destructive tracking-wide-luxury">
                          Rejected
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Status: {a.mixologist_track_status ?? "—"} ·{" "}
                      {a.mixologist_advanced_score !== null
                        ? `${a.mixologist_advanced_score}/${a.mixologist_advanced_total ?? 5}`
                        : "no attempt"}
                      {a.mixologist_cheating_strikes && a.mixologist_cheating_strikes > 0
                        ? ` · ${a.mixologist_cheating_strikes} strike(s)`
                        : ""}
                    </p>
                  </div>
                  {a.mixologist_reviewed_at && (
                    <span className="text-[10px] tracking-wide-luxury text-muted-foreground">
                      Reviewed {new Date(a.mixologist_reviewed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {a.mixologist_bar_manager_answer && (
                  <div className="mt-4">
                    <p className="tracking-wide-luxury text-xs text-accent">Bar Manager essay</p>
                    <p className="mt-2 text-sm whitespace-pre-wrap border-l-2 border-accent/40 pl-3">
                      {a.mixologist_bar_manager_answer}
                    </p>
                  </div>
                )}
                {a.latest_attempt && (
                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                    <div className="border border-border p-2">
                      <p className="text-muted-foreground">Last attempt</p>
                      <p className="font-medium">{new Date(a.latest_attempt.created_at).toLocaleString()}</p>
                    </div>
                    <div className="border border-border p-2">
                      <p className="text-muted-foreground">Score</p>
                      <p className="font-medium">
                        {a.latest_attempt.score ?? 0}/{a.latest_attempt.total ?? 0}
                      </p>
                    </div>
                    <div className="border border-border p-2">
                      <p className="text-muted-foreground">One-strike fail</p>
                      <p className={`font-medium ${a.latest_attempt.one_strike_terminated ? "text-destructive" : ""}`}>
                        {a.latest_attempt.one_strike_terminated ? "Yes" : "No"}
                      </p>
                    </div>
                    <div className="border border-border p-2">
                      <p className="text-muted-foreground">Events logged</p>
                      <p className="font-medium">{a.latest_attempt.events_count}</p>
                    </div>
                    {a.latest_attempt.time_remaining_per_question &&
                      Object.keys(a.latest_attempt.time_remaining_per_question).length > 0 && (
                        <div className="col-span-2 border border-border p-2 sm:col-span-4">
                          <p className="text-muted-foreground">Seconds left per question</p>
                          <p className="font-mono text-[11px] break-all">
                            {Object.entries(a.latest_attempt.time_remaining_per_question)
                              .map(([k, v]) => `${k.slice(0, 6)}…:${v}s`)
                              .join("  ")}
                          </p>
                        </div>
                      )}
                  </div>
                )}
                <Textarea
                  value={notes[a.id] ?? a.mixologist_review_notes ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                  placeholder="Review notes (optional)"
                  rows={2}
                  className="mt-4 rounded-none"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={() => onDecide(a.id, "approved")}
                    disabled={a.mixologist_track_status === "passed_mixologist"}
                    className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
                  >
                    {a.mixologist_track_status === "passed_mixologist"
                      ? "Approved ✓"
                      : "Approve as Mixologist"}
                  </Button>
                  <Button
                    onClick={() => onDecide(a.id, "rejected")}
                    variant="outline"
                    disabled={a.mixologist_track_status === "rejected_mixologist"}
                    className="rounded-none tracking-wide-luxury"
                  >
                    {a.mixologist_track_status === "rejected_mixologist"
                      ? "Rejected ✓"
                      : "Reject (keep as Bartender)"}
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </main>
    </div>
  );
}
